import { memo, useCallback, useMemo } from 'react';

import { merge } from '@/components';
import {
  Button,
  Checkbox,
  Flex,
  Heading,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  Text,
} from '@/components/core';
import { ChevronIcon } from '@/components/icons';
import { useValidationConfigStore } from '@/store';
import { capitalize, DivProps, OmitChildren } from '@/utils';

const CRON_HOURS = [...Array(24).keys()];
const CRON_WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const CRON_FREQUENCIES = ['within 5 minutes', 'max once per hour', 'max once per day'] as const;
type FrequencyType = (typeof CRON_FREQUENCIES)[number];

const parseWhen = (when?: string | null) => {
  const nextDay = (new Date().getDay() + 1) % CRON_WEEKDAYS.length;
  const defaultCron = {
    minute: '0',
    hour: '10',
    day: '?',
    month: '*',
    weekday: CRON_WEEKDAYS[nextDay],
    year: '*',
  };
  if (!when) return defaultCron;

  const match = /^cron\((.+)\)$/.exec(when);
  if (!match) return defaultCron;

  const parts = (match[1] ?? '').split(' ');
  if (parts.some((part) => !part)) return defaultCron;
  if (parts.length !== 6) return defaultCron;

  const [minute, hour, day, month, weekday, year] = parts;
  if (weekday.split(',').some((d) => !CRON_WEEKDAYS.includes(d))) return defaultCron;
  if (day !== '?' && weekday !== '?') return defaultCron;

  return { minute, hour, day, month, weekday, year };
};

export class Cron {
  minute: string;
  hour: string;
  day: string;
  month: string;
  weekday: string;
  year: string;
  timezone: string;

  constructor(when?: string | null, timezone?: string | null) {
    const { minute, hour, day, month, weekday, year } = parseWhen(when);
    this.minute = minute;
    this.hour = hour;
    this.day = day;
    this.month = month;
    this.weekday = weekday;
    this.year = year;
    this.timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  getEndHour() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, end] = this.hour.split('-');
    return end != null ? Number(end) : undefined;
  }

  getStartHour() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [start, _] = this.hour.split('-');
    return start != null ? Number(start) : undefined;
  }

  setEndHour(endHour?: string | number | null) {
    if (!endHour) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [start, _] = this.hour.split('-');
    this.hour = `${start}-${endHour}`;
  }

  setStartHour(startHour?: string | number | null) {
    if (!startHour) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, end] = this.hour.split('-');
    if (end != null) {
      this.hour = `${startHour}-${end}`;
    } else {
      this.hour = `${startHour}`;
    }
  }

  getDayChecked(day: string) {
    const checkedDays = this.weekday.split(',');
    return checkedDays.includes(day);
  }

  setDayChecked(day: string, checked: boolean) {
    const checkedDays = this.weekday.split(',');
    if (checked && !checkedDays.includes(day)) {
      this.weekday = [...checkedDays, day].join(',');
    } else if (!checked && checkedDays.includes(day)) {
      checkedDays.splice(checkedDays.indexOf(day), 1);
      this.weekday = checkedDays.join(',');
    }
  }

  getFrequency(): FrequencyType {
    if (this.minute === '*/5') return 'within 5 minutes';
    else if (this.getEndHour() == null) return 'max once per day';
    return 'max once per hour';
  }

  setFrequency(frequency: FrequencyType) {
    switch (frequency) {
      case 'within 5 minutes': {
        this.minute = '*/5';
        if (this.getEndHour() == null) {
          this.setEndHour(this.hour);
        }
        break;
      }
      case 'max once per hour': {
        this.minute = '0';
        if (this.getEndHour() == null) {
          this.setEndHour(this.hour);
        }
        break;
      }
      case 'max once per day': {
        this.minute = '0';
        const [start, _] = this.hour.split('-');
        this.hour = start;
        break;
      }
    }
  }

  cronExpression() {
    return `cron(${this.minute} ${this.hour} ${this.day} ${this.month} ${this.weekday} ${this.year})`;
  }
}

export type FrequencySettingProps = OmitChildren<DivProps> & {
  id: string;
};

const IFrequencySetting = ({ id, className, ...rest }: FrequencySettingProps) => {
  const updateNotification = useValidationConfigStore((state) => state.updateNotification);
  const notification = useValidationConfigStore((state) => state.getNotification(id));
  const cron = useMemo(
    () => new Cron(notification?.when, notification?.timezone),
    [notification?.when, notification?.timezone]
  );

  const updateCron = useCallback(
    (cron: Cron) => {
      updateNotification(id, (notification) => {
        return { ...notification, when: cron.cronExpression(), timezone: cron.timezone };
      });
    },
    [id, updateNotification]
  );

  const allowedStartHours = useMemo(() => {
    const endHour = cron.getEndHour();
    if (endHour == null) return CRON_HOURS;
    return CRON_HOURS.filter((h) => h <= endHour);
  }, [cron]);

  const allowedEndHours = useMemo(() => {
    const startHour = cron.getStartHour();
    if (startHour == null) return CRON_HOURS;
    return CRON_HOURS.filter((h) => h >= startHour);
  }, [cron]);

  const toClockHour = useCallback((h: number) => {
    const hour = h % 12 === 0 ? 12 : h % 12;
    return (hour < 10 ? '0' : '') + hour + ':00' + (h < 12 ? ' AM' : ' PM');
  }, []);

  const timezoneOptions = useMemo(() => {
    return Intl.supportedValuesOf('timeZone').map((tz) => (
      <ListBoxItem key={tz} id={tz}>
        <Text>{tz}</Text>
      </ListBoxItem>
    ));
  }, []);

  const setDayChecked = useCallback(
    (day: string, checked: boolean) => {
      cron.setDayChecked(day, checked);
      updateCron(cron);
    },
    [cron, updateCron]
  );

  return (
    <div className={merge('flex flex-col gap-2', className)} {...rest}>
      <div className="flex flex-col">
        <Heading size="xs">Frequency</Heading>
        <Text>Set how often you’d like to send notifications.</Text>
      </div>

      <div className="flex flex-col gap-2">
        {/* Frequency */}
        <Select
          className="grow"
          value={cron.getFrequency()}
          onChange={(frequency) => {
            cron.setFrequency(frequency?.toString() as FrequencyType);
            updateCron(cron);
          }}
        >
          <div>
            <Button variant="secondary">
              <SelectValue />
              <ChevronIcon rotation="down" />
            </Button>
          </div>

          <Popover>
            <ListBox className="max-h-64 overflow-y-auto">
              {CRON_FREQUENCIES.map((f) => (
                <ListBoxItem key={f} id={f}>
                  <Text>{capitalize(f)}</Text>
                </ListBoxItem>
              ))}
            </ListBox>
          </Popover>
        </Select>

        <Label>{cron.getEndHour() == null ? 'At' : 'Between'}</Label>

        <div className="flex w-full items-center gap-2">
          {/* Start Hour */}
          <Select
            className="grow"
            value={cron.getStartHour()}
            onChange={(startHour) => {
              cron.setStartHour(startHour?.toString());
              updateCron(cron);
            }}
          >
            <div>
              <Button variant="secondary">
                <SelectValue />
                <ChevronIcon rotation="down" />
              </Button>
            </div>

            <Popover>
              <ListBox className="max-h-64 overflow-y-auto">
                {allowedStartHours.map((i) => (
                  <ListBoxItem key={i} id={i}>
                    <Text>{toClockHour(i)}</Text>
                  </ListBoxItem>
                ))}
              </ListBox>
            </Popover>
          </Select>

          {/* End Hour */}
          {cron.getEndHour() != null ? (
            <>
              <Label>and</Label>

              <Select
                className="grow"
                value={cron.getEndHour()}
                onChange={(endHour) => {
                  cron.setEndHour(endHour?.toString());
                  updateCron(cron);
                }}
              >
                <div>
                  <Button variant="secondary">
                    <SelectValue />
                    <ChevronIcon rotation="down" />
                  </Button>
                </div>

                <Popover>
                  <ListBox className="max-h-64 overflow-y-auto">
                    {allowedEndHours.map((i) => (
                      <ListBoxItem key={i} id={i}>
                        <Text>{toClockHour(i)}</Text>
                      </ListBoxItem>
                    ))}
                  </ListBox>
                </Popover>
              </Select>
            </>
          ) : null}
        </div>

        <Flex variant="secondary" className="justify-evenly gap-2 rounded-lg p-2">
          {CRON_WEEKDAYS.map((day, i) => (
            <>
              {i > 0 ? <div className="w-[1px] grow-0 bg-gray-200" /> : null}
              <div className="flex flex-col items-center gap-2">
                <Checkbox
                  key={day}
                  isSelected={cron.getDayChecked(day)}
                  onChange={(isSelected) => setDayChecked(day, isSelected)}
                />
                <Text>{capitalize(day)}</Text>
              </div>
            </>
          ))}
        </Flex>
      </div>

      <div className="flex flex-col gap-2">
        <Label>Timezone</Label>

        {/* Timezone */}
        <Select
          className="grow"
          value={cron.timezone}
          onChange={(timezone) => {
            cron.timezone = timezone?.toString() ?? cron.timezone;
            updateCron(cron);
          }}
        >
          <div>
            <Button variant="secondary">
              <SelectValue />
              <ChevronIcon rotation="down" />
            </Button>
          </div>

          <Popover>
            <ListBox className="max-h-64 overflow-y-auto">{timezoneOptions}</ListBox>
          </Popover>
        </Select>
      </div>
    </div>
  );
};

export const FrequencySetting = memo(IFrequencySetting) as typeof IFrequencySetting;
